import { useCallback, useLayoutEffect, useRef } from 'react';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { isDerivedDose } from '~/domain/dose/derivedDose';
import { isDerivedStructureSet } from '~/domain/patient/derivedStructureSet';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import {
    replanningRigidStructuresState,
    replanningRigidStructuresStatusState,
} from '~/state/atoms/adaptiveReplanningAtoms';

// Removes the rigidly moved planning structures and every dose computed on them.
export function useDiscardRigidStructures(): () => void {
    const patient = useSelectedPatient();
    const { deleteDose, deleteStructureSet } = useDeleteDataset();
    const [record, setRecord] = useRecoilState(replanningRigidStructuresState);
    const resetStatus = useResetRecoilState(
        replanningRigidStructuresStatusState
    );

    // Held: discarding runs from an unmount, after these have moved on.
    const held = useRef({ patient, deleteDose, deleteStructureSet, record });
    useLayoutEffect(() => {
        held.current = { patient, deleteDose, deleteStructureSet, record };
    });

    return useCallback(() => {
        const current = held.current;
        const set = current.record
            ? current.patient?.structureSets.find(
                  (candidate) =>
                      candidate.dicomUid === current.record?.structureSetUid
              )
            : undefined;
        if (set && isDerivedStructureSet(set)) {
            for (const dose of [...(current.patient?.doses ?? [])]) {
                if (isDerivedDose(dose) && dose.structureSet === set) {
                    current.deleteDose(dose);
                }
            }
            current.deleteStructureSet(set);
        }
        setRecord(undefined);
        resetStatus();
    }, [setRecord, resetStatus]);
}
